import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updatePatientProfileThunk } from '../../store/slices/patientProfilesReducer';
import { useTranslation } from 'react-i18next';
import { useAppAlertDialog } from '../../hooks/useAppAlertDialog';

const PatientCard = ({ patient, onDelete }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const { showAlert, showConfirm, dialogNode } = useAppAlertDialog({
    cancelLabel: t("Avbryt"),
    confirmLabel: t("Fortsätt"),
    closeLabel: t("Stäng"),
  });
  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const handleToggleStatus = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const newStatus = patient.status === 'active' ? 'inactive' : 'active';
    
    setUpdatingStatus(true);
    try {
      await dispatch(updatePatientProfileThunk({
        id: patient.id,
        profileData: { status: newStatus }
      })).unwrap();
    } catch (error) {
      showAlert(t("Fel"), t('Kunde inte uppdatera patientstatus. Försök igen.'));
      console.error('Failed to update patient status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300 transform hover:scale-[1.02] group">
      {/* Patient Header */}
      <div className="relative h-32 mobile:h-40 tablet:h-48 laptop:h-48 desktop:h-48 bg-gradient-to-br from-blue-100 to-blue-200 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-green/20 to-secondary-green/30 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mobile:w-20 mobile:h-20 tablet:w-20 tablet:h-20 laptop:w-20 laptop:h-20 desktop:w-20 desktop:h-20 bg-white/80 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl mobile:text-3xl tablet:text-4xl laptop:text-4xl desktop:text-4xl font-bold text-secondary-green">
                {patient.name ? patient.name.charAt(0).toUpperCase() : '?'}
              </span>
            </div>
          </div>
        </div>

        {/* Delete Button */}
        {onDelete && (
          <button
            type="button"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              const confirmed = await showConfirm(
                t("Ta bort patient"),
                t('Är du säker på att du vill ta bort {{name}}?', { name: patient.name })
              );
              if (confirmed) {
                onDelete(patient);
              }
            }}
            className="absolute bottom-2 right-2 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/80 hover:bg-white/90 border border-gray-200 shadow-sm transition-colors"
            title={t("Radera patient")}
          >
            <img 
              src="/assets/icons/Radera.png" 
              alt={t("Ta bort")} 
              className="w-4 h-4 text-gray-600"
            />
          </button>
        )}

        {/* Patient Name Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 mobile:p-4 tablet:p-4 laptop:p-4 desktop:p-4 pointer-events-none">
          <h3 className="text-sm mobile:text-base tablet:text-lg laptop:text-lg desktop:text-lg font-bold text-white truncate">
            {patient.name}
          </h3>
        </div>

        {/* Status Badge with Toggle */}
        <div className="absolute top-2 mobile:top-3 tablet:top-3 laptop:top-3 desktop:top-3 right-2 mobile:right-3 tablet:right-3 laptop:right-3 desktop:right-3">
          <button
            onClick={handleToggleStatus}
            disabled={updatingStatus}
            className={`px-2 mobile:px-3 tablet:px-3 laptop:px-3 desktop:px-3 py-1 mobile:py-1 tablet:py-1 laptop:py-1 desktop:py-1 rounded-full text-xs mobile:text-xs tablet:text-xs laptop:text-xs desktop:text-xs font-semibold transition-all hover:shadow-md ${getStatusColor(patient.status)} ${updatingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            title={patient.status === 'active' ? t('Klicka för att markera som inaktiv') : t('Klicka för att markera som aktiv')}
          >
            {updatingStatus ? (
              <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            ) : (
              patient.status === 'active' ? t('Aktiva') : t('Inaktiva')
            )}
          </button>
        </div>
      </div>

      {/* Patient Info */}
      <div className="p-3 mobile:p-4 tablet:p-4 laptop:p-4 desktop:p-4">
        <div className="space-y-1 mobile:space-y-2 tablet:space-y-2 laptop:space-y-2 desktop:space-y-2 mb-2 mobile:mb-3 tablet:mb-3 laptop:mb-3 desktop:mb-3">
          {patient.age && (
            <div className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-gray-600">
              {t("Ålder")}: {patient.age} {t("år")}
            </div>
          )}
          {patient.address && (
            <div className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-gray-600 truncate">
              {patient.address}
            </div>
          )}
        </div>

        <Link 
          to={`/kostdata/patients/${patient.id}`}
          className="block w-full bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 mobile:py-2 tablet:py-2 laptop:py-2 desktop:py-2 px-3 mobile:px-4 tablet:px-4 laptop:px-4 desktop:px-4 rounded-lg mobile:rounded-lg tablet:rounded-lg laptop:rounded-lg desktop:rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm text-center"
        >
          {t('Visa profilen')}
        </Link>
      </div>
      {dialogNode}
    </div>
  );
};

export default PatientCard;

